import { useMemo, useRef, useState } from 'react';
import { AlertCircle, AlertTriangle, Pencil, Plus, Search, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  type RegraDeParaIBSCBSApi,
  useImportarConteudoFiscalIbsCbs,
  useRegrasIBSCBS,
  useRegrasIbsCbsEntrada,
  useSalvarRegraIBSCBS,
} from '@/services/Configuracoes';
import { ClassificacaoIbsCbsDialog } from './ClassificacaoIbsCbsDialog';
import {
  faltaImportar,
  filtrarLinhasRegrasIbsCbs,
  montarLinhasRegrasIbsCbs,
} from './regras-ibs-cbs';

const POR_PAGINA = 25;

export function RegrasIbsCbsEntrada() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [busca, setBusca] = useState('');
  const [pagina, setPagina] = useState(1);

  const classificacoes = useRegrasIBSCBS();
  const regras = useRegrasIbsCbsEntrada();
  const importar = useImportarConteudoFiscalIbsCbs();
  const salvar = useSalvarRegraIBSCBS();
  const [dialogo, setDialogo] = useState<{ linha?: RegraDeParaIBSCBSApi } | null>(null);

  const linhas = useMemo(
    () => montarLinhasRegrasIbsCbs(classificacoes.data?.data ?? [], regras.data?.data ?? []),
    [classificacoes.data, regras.data]
  );
  const filtradas = useMemo(() => filtrarLinhasRegrasIbsCbs(linhas, busca), [linhas, busca]);
  const totalDePaginas = Math.max(1, Math.ceil(filtradas.length / POR_PAGINA));
  const paginaAtual = Math.min(pagina, totalDePaginas);
  const visiveis = filtradas.slice((paginaAtual - 1) * POR_PAGINA, paginaAtual * POR_PAGINA);
  const aviso = faltaImportar(linhas.length, regras.data?.data?.length ?? 0);

  const handleArquivo = (event: React.ChangeEvent<HTMLInputElement>) => {
    const arquivo = event.target.files?.[0];
    event.target.value = '';
    if (!arquivo) return;

    const leitor = new FileReader();
    leitor.onload = () => {
      importar.mutate({ NomeArquivo: arquivo.name, Conteudo: String(leitor.result ?? '') });
    };
    leitor.readAsText(arquivo, 'utf-8');
  };

  const handleSalvar = (linha: Partial<RegraDeParaIBSCBSApi>) => {
    salvar.mutate(linha, {
      onSuccess: resposta => {
        if (resposta.status.resultado) setDialogo(null);
      },
    });
  };

  if (classificacoes.isLoading || regras.isLoading) {
    return (
      <div className="space-y-3" aria-label="Carregando regras de IBS/CBS">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        className="hidden"
        data-testid="arquivo-regras-ibs-cbs"
        onChange={handleArquivo}
      />

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Pesquisar por cClassTrib, CST, regra, descrição..."
            value={busca}
            onChange={e => {
              setBusca(e.target.value);
              setPagina(1);
            }}
            className="pl-9 h-9"
          />
        </div>
        <div className="flex-1" />
        <Button variant="outline" size="sm" className="gap-1.5 h-9" onClick={() => setDialogo({})}>
          <Plus className="h-3.5 w-3.5" />
          Incluir classificação
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 h-9"
          disabled={importar.isPending}
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="h-3.5 w-3.5" />
          {importar.isPending ? 'Importando...' : 'Importar Arquivo...'}
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">
        Toda empresa começa com o padrão da casa (as 22 regras e as 173 classificações do De/Para).
        A linha alterada ou incluída aqui deixa de seguir as versões novas do padrão. A importação
        aceita os dois arquivos do De/Para (CSV separado por ponto e vírgula) e substitui o que
        havia do mesmo arquivo.
      </p>

      {aviso && (
        <div
          role="status"
          className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800"
        >
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{aviso}</span>
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        {filtradas.length} classificaç{filtradas.length === 1 ? 'ão' : 'ões'}
      </p>

      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="w-24">cClassTrib</TableHead>
              <TableHead className="w-16">CST</TableHead>
              <TableHead className="min-w-[220px]">Descrição</TableHead>
              <TableHead className="w-24">Regra</TableHead>
              <TableHead className="w-44">Vigência</TableHead>
              <TableHead className="w-20 text-right">Red. IBS</TableHead>
              <TableHead className="w-20 text-right">Red. CBS</TableHead>
              <TableHead className="w-36">Créd. IBS</TableHead>
              <TableHead className="w-36">Créd. CBS</TableHead>
              <TableHead className="w-28">Regime regular</TableHead>
              <TableHead className="min-w-[180px]">Uso admitido</TableHead>
              <TableHead className="min-w-[200px]">Origem</TableHead>
              <TableHead className="w-12">
                <span className="sr-only">Alterar</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visiveis.length === 0 ? (
              <TableRow>
                <TableCell colSpan={13} className="text-center py-8 text-muted-foreground text-sm">
                  Nenhuma classificação encontrada
                </TableCell>
              </TableRow>
            ) : (
              visiveis.map(linha => (
                <TableRow key={linha.id} className="hover:bg-muted/30">
                  <TableCell className="font-mono text-sm font-medium">{linha.classTrib}</TableCell>
                  <TableCell className="font-mono text-sm">
                    <div>{linha.cst}</div>
                    {linha.cstDivergente && (
                      <div
                        className="mt-1 flex items-center gap-1 font-sans text-xs text-amber-700"
                        title={`O CST da linha é ${linha.cst}; a classificação ${linha.classTrib} manda ${linha.cstEsperado}.`}
                      >
                        <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden="true" />
                        <span>CST diferente da classificação (esperado {linha.cstEsperado})</span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-sm" title={linha.tratamento}>
                    {linha.descricao}
                  </TableCell>
                  <TableCell className="text-sm" title={linha.nomeRegra ?? undefined}>
                    {linha.regra}
                  </TableCell>
                  <TableCell className="text-sm">{linha.vigencia}</TableCell>
                  <TableCell className="font-mono text-sm text-right">{linha.reducaoIbs}</TableCell>
                  <TableCell className="font-mono text-sm text-right">{linha.reducaoCbs}</TableCell>
                  <TableCell className="text-sm">{linha.creditoIbs}</TableCell>
                  <TableCell className="text-sm">{linha.creditoCbs}</TableCell>
                  <TableCell className="text-sm">{linha.regimeRegular}</TableCell>
                  <TableCell className="text-sm">{linha.usos}</TableCell>
                  <TableCell className="text-sm">
                    <div className="flex items-center gap-1.5">
                      <span>{linha.origem}</span>
                      {linha.versaoNovaDoPadrao && (
                        <AlertTriangle
                          className="h-3.5 w-3.5 shrink-0 text-amber-600"
                          role="img"
                          aria-label="Há versão nova do padrão para esta linha"
                        >
                          <title>Há versão nova do padrão para esta linha</title>
                        </AlertTriangle>
                      )}
                    </div>
                    {linha.detalheDaOrigem && (
                      <div className="text-xs text-muted-foreground">{linha.detalheDaOrigem}</div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      aria-label={`Alterar ${linha.classTrib}`}
                      onClick={() => setDialogo({ linha: linha.api })}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <ClassificacaoIbsCbsDialog
        aberto={!!dialogo}
        {...(dialogo?.linha ? { linha: dialogo.linha } : {})}
        regras={regras.data?.data ?? []}
        salvando={salvar.isPending}
        onFechar={() => setDialogo(null)}
        onSalvar={handleSalvar}
      />

      {totalDePaginas > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <Button
            variant="outline"
            size="sm"
            disabled={paginaAtual === 1}
            onClick={() => setPagina(paginaAtual - 1)}
          >
            Anterior
          </Button>
          <span className="text-muted-foreground">
            Página {paginaAtual} de {totalDePaginas}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={paginaAtual === totalDePaginas}
            onClick={() => setPagina(paginaAtual + 1)}
          >
            Próxima
          </Button>
        </div>
      )}
    </div>
  );
}
